import * as React from "react";
import "./SelectCtl.scss";
import { ListItem } from "@testout/testout-commerce/models/ListItem";


interface ISelectProps {
    disabled?: boolean;
    value?: string;
    onChange?: (value: string) => void;
    items: ListItem[];
    placeholder?: string;
    className?: string;
    style?: React.CSSProperties;
    hideItemValue?: boolean;
    multiSelect?: boolean;
    errorMessage?: string;
}

interface ISelectState {
    isOpen: boolean;
    focusIndex: number;
    selectedIndexes: number[];
}


export default class SelectCtl extends React.PureComponent<ISelectProps, ISelectState> {
    constructor(props: ISelectProps) {
        super(props);
        this.state = { isOpen: false, focusIndex: -1, selectedIndexes: [] };
    }

    _selectRef = React.createRef<HTMLDivElement>();
    _valueRef = React.createRef<HTMLDivElement>();
    _scrollerRef = React.createRef<HTMLDivElement>();
    _itemRef = React.createRef<HTMLLIElement>();
    _searchPrefix: string = "";
    _searchTimeout: number = 0;
    _needFocus = false;

    static separator = ",";

    static getDerivedStateFromProps(props: ISelectProps) {
        let selectedIndexes: number[] = [];
        if (props.value)
            for (const value of props.value.split(SelectCtl.separator)) 
                if (value) 
                    selectedIndexes.push(props.items.findIndex((item: ListItem) => (item.value || item.name) === value));
        return { selectedIndexes: selectedIndexes };
    }

    getValueIndex(value?: string, prefixMatch: boolean = false): number {
        if (!value) return -1;
        if (prefixMatch)
            return this.props.items.findIndex((item: ListItem) =>
                (item.value || item.name).toUpperCase().startsWith(value) ||
                item.name.toUpperCase().indexOf(value) >= 0);
        else
            return this.props.items.findIndex((item: ListItem) => (item.value || item.name) === value);
    }

    addSelectedIndex(itemIndex: number): string {
        if (this.state.selectedIndexes.indexOf(itemIndex) >= 0) return this.props.value ?? "";
        const item = this.props.items[itemIndex];
        return `${this.props.multiSelect ? `${this.props.value}${this.props.value ? SelectCtl.separator : ""}` : ""}${item.value || item.name}`;
    }

    removeSelectedIndex(itemIndex: number): string {
        if (this.state.selectedIndexes.indexOf(itemIndex) < 0) return this.props.value ?? "";
        const item = this.props.items[itemIndex];
        const value = `${SelectCtl.separator}${this.props.value}${SelectCtl.separator}`
            .replace(new RegExp(`${SelectCtl.separator}${item.value || item.name}${SelectCtl.separator}`), SelectCtl.separator);
        return value.substring(1, value.length-1);
    }

    onSelectClick = (e: React.MouseEvent<HTMLDivElement>) => {
        //e.stopPropagation();

        if (this.props.disabled) return;
        if (this.state.isOpen)
            this.setState({ isOpen: false });
        else
            this.setState({ isOpen: true, focusIndex: this.getValueIndex(this.props.value) });
    }

    onSelectKeyDown = (e: React.KeyboardEvent) => {
        if (this.props.disabled) return;
        if (e.key === "Enter") {
            if (!this.state.isOpen)
                this.setState({ isOpen: true, focusIndex: this.getValueIndex(this.props.value) });
            else if (this.state.focusIndex < 0)
                this.setState({ isOpen: false });
            else {
                if (!this.props.multiSelect)
                    this.setState({ isOpen: false });
                this.props.onChange?.(this.props.multiSelect && this.state.selectedIndexes.indexOf(this.state.focusIndex) >= 0 
                    ? this.removeSelectedIndex(this.state.focusIndex) 
                    : this.addSelectedIndex(this.state.focusIndex));
            }
            e.preventDefault();
        } else if (e.key === "Escape") {
            if (this.state.isOpen) 
                this.setState({ isOpen: false });
            e.preventDefault();
        } else if (e.key === "ArrowDown") {
            const focusIndex = this.state.isOpen ? this.state.focusIndex : this.getValueIndex(this.props.value);
            if (focusIndex < this.props.items.length - 1)
                if (this.state.isOpen)
                    this.setState({ focusIndex: focusIndex + 1 });
                else if (!this.props.multiSelect) {
                    this.setState({ isOpen: false });
                    this.props.onChange?.(this.addSelectedIndex(focusIndex + 1));
                }
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            const focusIndex = this.state.isOpen ? this.state.focusIndex : this.getValueIndex(this.props.value);
            if (focusIndex > 0)
                if (this.state.isOpen)
                    this.setState({ focusIndex: focusIndex - 1 });
                else if (!this.props.multiSelect) {
                    this.setState({ isOpen: false });
                    this.props.onChange?.(this.addSelectedIndex(focusIndex - 1));
                }
            e.preventDefault();
        } else if (this.state.isOpen && /^[ -~]$/.test(e.key)) {

            // Focus the first item that starts with the collected searchPrefix
            this._searchPrefix += e.key.toUpperCase();
            this.setState({ focusIndex: this.getValueIndex(this._searchPrefix, true) });

            // Clear any current timeout and set a timer to clear the collected 
            // searchPrefix if no additional keys are received in the next second
            if (this._searchTimeout) window.clearTimeout(this._searchTimeout);
            this._searchTimeout = window.setTimeout(() => {
                    window.clearTimeout(this._searchTimeout);
                    this._searchPrefix = "";
                    this._searchTimeout = 0;
                }, 1000);
            e.preventDefault();
        }
    }

    onItemClick = (itemIndex: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        this._needFocus = true;
        if (!this.props.multiSelect)
            this.setState({ isOpen: false });
        this.props.onChange?.(this.props.multiSelect && this.state.selectedIndexes.indexOf(itemIndex) >= 0 
            ? this.removeSelectedIndex(itemIndex) 
            : this.addSelectedIndex(itemIndex));
    }

    onItemToggleClick = (itemIndex: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        this._needFocus = true;
        this.props.onChange?.(this.state.selectedIndexes.indexOf(itemIndex) >= 0 
            ? this.removeSelectedIndex(itemIndex) 
            : this.addSelectedIndex(itemIndex));
    }

    onItemRemoveClick = (itemIndex: number, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        this.props.onChange?.(this.removeSelectedIndex(itemIndex));
    }

    onDocumentFocusIn = (e: Event) => {
        // Close the drop-down if focus/click outside of select control
        const divSelect = this._selectRef.current;
        if (divSelect && !divSelect.contains(e.target as Node) && this.state.isOpen && 
            (e.target as Node).getRootNode() === document) // Do not close if click was on node that has been removed from DOM tree
            this.setState({ isOpen: false });
    }

    ensureItemVisible() {
        if (this.state.isOpen && this._scrollerRef.current) {
            const divScroller = this._scrollerRef.current as HTMLDivElement;
            if (this._itemRef.current && this.state.focusIndex >= 0) {
                const liItem = this._itemRef.current as HTMLLIElement;
                if (liItem.offsetTop < divScroller.scrollTop)
                    divScroller.scrollTop = liItem.offsetTop;
                if (liItem.offsetTop + liItem.offsetHeight > divScroller.scrollTop + divScroller.clientHeight)
                    divScroller.scrollTop = liItem.offsetTop + liItem.offsetHeight - divScroller.clientHeight;
            }

            const divValue = this._valueRef.current;
            let r1 = document.body.getBoundingClientRect();
            let r2 = divScroller.getBoundingClientRect();
            divScroller.style.bottom = (r2.top + r2.height > r1.height)
                ? `${(divValue?.clientHeight ?? 0) + 4}px`
                : "";
        }
    }

    componentDidMount() {
        document.addEventListener("focusin", this.onDocumentFocusIn);
        document.addEventListener("click", this.onDocumentFocusIn);
        this.ensureItemVisible();
    }

    componentDidUpdate() {
        this.ensureItemVisible();
        if (this._needFocus) {
            this._needFocus = false;
            const divValue = this._valueRef.current as HTMLDivElement;
            divValue.focus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("focusin", this.onDocumentFocusIn);
        document.removeEventListener("click", this.onDocumentFocusIn);
    }

    render() {
        const { focusIndex, isOpen, selectedIndexes } = this.state;
        const { items, disabled, style, placeholder, hideItemValue, multiSelect, errorMessage, className } = this.props;
        const selectedValues = this.props.value ? this.props.value.split(SelectCtl.separator).filter((value) => value) : [];

        return <div className={`select${disabled ? " disabled" : ""}${className ? " " + className : ""}`}
                    style={style} ref={this._selectRef}>
            <div className={`${multiSelect ? "values" : "value"}${!selectedIndexes.length ? " empty" : ""}${errorMessage ? " invalid" : ""}${disabled ? " disabled" : ""}`}
                        ref={this._valueRef}
                        tabIndex={disabled ? undefined : 0}
                        onClick={this.onSelectClick}
                        onKeyDown={this.onSelectKeyDown}>
                {!selectedIndexes.length && placeholder}
                {selectedIndexes.map((i: number, si: number) =>
                    i >= 0
                        ? <div key={i}>{items[i].name}
                            {items[i].value && !hideItemValue && <span className={`span${disabled ? " disabled" : ""}`}>{items[i].value}</span>}
                            {multiSelect && <div className="x" onClick={(e: React.MouseEvent<HTMLDivElement>) => this.onItemRemoveClick(selectedIndexes[si], e)} />}
                          </div>
                        : <div key={selectedValues[si]}>{selectedValues[si]}</div>)}
                   </div>{errorMessage && <div className="errorDiv">{errorMessage}</div>}
                   {isOpen &&
                       <div className="scroller" ref={this._scrollerRef}>
                           <ul>
                               {items.map((item: ListItem, i) =>
                                   <li key={i} ref={i === focusIndex ? this._itemRef : undefined}
                                       className={i === focusIndex ? "focus" : undefined}
                                       onClick={(e: React.MouseEvent<HTMLLIElement>) => this.onItemClick(i, e) }>
                                       {multiSelect && 
                                           <div className={`check${selectedIndexes.indexOf(i) >= 0 ? " checked" : ""}`} 
                                                onClick={(e: React.MouseEvent<HTMLDivElement>) => this.onItemToggleClick(i, e)}/>}
                                       <div>
                                           {item.name}
                                           {item.value && item.value !== item.name && !hideItemValue &&
                                               <span className={`span${disabled ? " disabled" : ""}`}>{item.value}</span>}
                                           {item.description &&
                                               <div>{item.description}</div>}
                                       </div>
                                   </li>)}
                           </ul>
                       </div>}
               </div>;
    }
}